import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NavLink,
  useLocation,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { format } from 'date-fns';
import {
  TableMenu,
  IconLoader,
  Table,
  useQueryState,
  Filters,
  useFilters,
  IconArrowLeft,
  SchemaFilterType,
  useScroll,
  DateInputRange,
  usePrevious,
  useGetList,
  SelectItemProps,
  SortDir,
  ToggleInput,
  // InputText,
  // InputSize,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import useGetAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import {
  useLazyGetCategoriesQuery,
  useLazyGetAccountsReportQuery,
  useLazyGetBalanceSheetAccountsQuery,
} from './store/service';
import { clearAccountsReportState } from './store/slices/accounts';
import {
  schema,
  filtersSchema,
  columns,
  columnsLabels,
} from './variables/list';
import { AccountsReport } from './types';
import formatReportBalance from '../helpers/format-report-balance';
import PrecisionSelector from '../../components/partial/reports/precision-selector';

import ROUTES from '../../routes';

import cn from './accounts-report.module.css';

const formatReport = (list: AccountsReport[]): AccountsReport[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      closed: <div>{item.closed ? 'Closed' : 'Open'}</div>,
      debitIn: (
        <div>
          {item?.debitIn?.toString() ? formatReportBalance(item?.debitIn) : '-'}
        </div>
      ),
      debitOut: (
        <div>
          {item?.debitOut?.toString()
            ? formatReportBalance(item?.debitOut)
            : '-'}
        </div>
      ),
      debitTo: (
        <div>
          {item?.debitTo?.toString() ? formatReportBalance(item?.debitTo) : '-'}
        </div>
      ),
      creditIn: (
        <div>
          {item?.creditIn?.toString()
            ? formatReportBalance(item?.creditIn)
            : '-'}
        </div>
      ),
      creditOut: (
        <div>
          {item?.creditOut?.toString()
            ? formatReportBalance(item?.creditOut)
            : '-'}
        </div>
      ),
      creditTo: (
        <div>
          {item?.creditTo?.toString()
            ? formatReportBalance(item?.creditTo)
            : '-'}
        </div>
      ),
      accountType: (
        <div>
          {`${item?.accountType?.charAt(0)}${item?.accountType
            ?.slice(1)
            .toLowerCase()}`}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const AccountsBalanceReport = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { search, state: locationState } = useLocation();
  const [stateProps, setStateProps] = useState('');
  const [menuItemsCategories, setMenuItemsCategories] = useState<
    SelectItemProps[]
  >([]);
  const [menuItemsGroups, setMenuItemsGroups] = useState<SelectItemProps[]>([]);
  const [menuItemsPrimaries, setMenuItemsPrimaries] = useState<
    SelectItemProps[]
  >([]);
  const [dates, setDates] = useState<string[]>([]);
  const [excludeRevaluation, setExcludeRevaluation] = useState(false);
  const [reportLink, setReportLink] = useState('');
  // const [daysOffset, setDaysOffset] = useState('');
  const [roundDecimals, setRoundDecimals] = useState('2');

  const { autocompleteList, onSearch } = useGetAutocompleteList();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const {
    balance: {
      accountsReport: { menuItems, report, reportPagination, accounts },
    },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const [searchParams] = useSearchParams();

  const categoryId = searchParams.get('categoryId');
  const groupId = searchParams.get('groupId');
  const primaryId = searchParams.get('primaryId');

  const formattedData = useMemo(() => {
    const data = formatReport(report as AccountsReport[]);

    return data;
  }, [report, sort]);

  const [getAccountsList, { isLoading, isFetching }] =
    useLazyGetBalanceSheetAccountsQuery();

  const [getAccountsCategories] = useLazyGetCategoriesQuery();

  const [
    getAccountsReport,
    { isLoading: reportIsLoading, isFetching: reportIsFetching },
  ] = useLazyGetAccountsReportQuery();

  const { getListAction, onFilter, onSort, setPage, errorList } = useGetList({
    getList: getAccountsList,
    onFilterChange,
    onSortChange,
    schema,
  });

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    onSearch(searchAutocomplete);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const onFilterReport = ({
    excludeRevaluationValue,
    transferDateFrom,
    transferDateTo,
    daysOffsetValue,
    roundDecimalsValues,
  }: {
    excludeRevaluationValue?: boolean;
    transferDateFrom?: string;
    transferDateTo?: string;
    daysOffsetValue?: string;
    roundDecimalsValues?: string;
  }) => {
    const ids = accounts?.map((item) => item.id);

    if (ids?.length) {
      let accountsReportQuery = `?accountId=${ids?.join('&accountId=')}`;

      if (excludeRevaluationValue) {
        accountsReportQuery = `${accountsReportQuery}&excludeRevaluation=${excludeRevaluationValue}`;
      }

      if (transferDateFrom) {
        accountsReportQuery = `${accountsReportQuery}&transferDateFrom=${transferDateFrom}`;
      }

      if (transferDateTo) {
        accountsReportQuery = `${accountsReportQuery}&transferDateTo=${transferDateTo}`;
      }

      if (daysOffsetValue) {
        accountsReportQuery = `${accountsReportQuery}&daysOffset=${daysOffsetValue}`;
      }

      if (roundDecimalsValues) {
        accountsReportQuery = `${accountsReportQuery}&roundDecimals=${roundDecimalsValues}`;
      }

      let queryValue = search
        ?.split('&')
        ?.filter(
          (item) =>
            !item.includes('excludeRevaluation') &&
            !item.includes('transferDateFrom') &&
            !item.includes('transferDateTo') &&
            !item.includes('daysOffset'),
        )
        .join('&');

      if (transferDateFrom) {
        queryValue = `${queryValue}&transferDateFrom=${transferDateFrom}`;
      }

      if (transferDateTo) {
        queryValue = `${queryValue}&transferDateTo=${transferDateTo}`;
      }

      if (excludeRevaluationValue) {
        queryValue = `${queryValue}&excludeRevaluation=${excludeRevaluationValue}`;
      }

      if (daysOffsetValue) {
        queryValue = `${queryValue}&daysOffset=${daysOffsetValue}`;
      }

      if (roundDecimalsValues) {
        queryValue = `${queryValue}&roundDecimals=${roundDecimalsValues}`;
      }

      navigate(`?${queryValue.replace('?', '')}`);

      getAccountsReport(accountsReportQuery);
    }
  };

  // useEffect(() => {
  //   if (isSuccess) {
  //     getStatsAction();
  //   }
  // }, [isSuccess, getStatsAction]);

  useEffect(() => {
    if (
      sort &&
      (search || (!search.includes('categoryId') && sort)) &&
      prevFilters &&
      filters &&
      Object.keys(filters).length &&
      !accounts
    ) {
      getListAction({
        filters: {
          categoryId: {
            value: [filters?.categoryId?.value[0]],
            type: SchemaFilterType.STRING,
          },
          groupId: {
            value: [filters?.groupId?.value[0]],
            type: SchemaFilterType.STRING,
          },
          primaryId: {
            value: [filters?.primaryId?.value[0]],
            type: SchemaFilterType.STRING,
          },
        },
        sort,
        withoutTimeZone: true,
      });

      // if (filters?.daysOffset) {
      //   setDaysOffset(filters?.daysOffset?.value[0]);
      // }

      if (filters.transferDate) {
        setDates(filters.transferDate.value);
      }

      if (filters?.excludeRevaluation) {
        setExcludeRevaluation(
          Boolean(filters?.excludeRevaluation?.value[0] === 'true'),
        );
      }

      if (filters?.roundDecimals) {
        setRoundDecimals(filters?.roundDecimals?.value[0]);
      }
    }
  }, [filters, sort, search, prevFilters]);

  useEffect(() => {
    if (menuItems) {
      const categoriesItem: SelectItemProps[] = [];
      const groupsItem: SelectItemProps[] = [];
      const primariesItem: SelectItemProps[] = [];

      menuItems?.forEach((item) => {
        categoriesItem.push({
          name: item.category.label,
          value: item.category.id,
        });

        item.category?.groups?.forEach((group) => {
          groupsItem.push({
            name: group.label,
            value: group.id,
          });

          group?.accounts?.forEach((account) => {
            primariesItem.push({
              name: account.label,
              value: account.id,
            });
          });
        });
      });

      setMenuItemsCategories(categoriesItem);
      setMenuItemsGroups(groupsItem);
      setMenuItemsPrimaries(primariesItem);
    }
  }, [menuItems]);

  useEffect(() => {
    setValues('description', autocompleteList);
    setValues('categoryId', menuItemsCategories);
    setValues('groupId', menuItemsGroups);
    setValues('primaryId', menuItemsPrimaries);
  }, [
    autocompleteList,
    menuItemsCategories,
    menuItemsGroups,
    menuItemsPrimaries,
  ]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (accounts) {
      const ids = accounts?.map((item) => item.id);

      if (ids?.length) {
        let accountsReportQuery = `?accountId=${ids?.join('&accountId=')}`;

        if (excludeRevaluation) {
          accountsReportQuery = `${accountsReportQuery}&excludeRevaluation=${excludeRevaluation}`;
        }

        if (dates?.length) {
          accountsReportQuery = `${accountsReportQuery}&transferDateFrom=${dates[0]}`;

          if (dates?.length > 1) {
            accountsReportQuery = `${accountsReportQuery}&transferDateTo=${dates[1]}`;
          }
        }

        // if (daysOffset) {
        //   accountsReportQuery = `${accountsReportQuery}&daysOffset=${daysOffset}`;
        // }

        if (roundDecimals) {
          accountsReportQuery = `${accountsReportQuery}&roundDecimals=${roundDecimals}`;
        }

        getAccountsReport(accountsReportQuery);
      }
    }
  }, [accounts]);

  const otherSearch = useMemo(
    () =>
      search
        .substring(1)
        .split('&')
        .filter(
          (item) =>
            !item.includes('categoryId') &&
            !item.includes('groupId') &&
            !item.includes('primaryId'),
        )
        .join('&'),
    [search],
  );

  useEffect(() => {
    if (!menuItems) {
      getAccountsCategories();
    }
  }, [menuItems]);

  useEffect(() => {
    let title = 'Accounts report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(() => {
    if (locationState && locationState.from) {
      setStateProps(locationState.from);
    }
  }, [locationState]);

  useEffect(() => {
    const reportLinkValue = localStorage.getItem('reportLink');

    if (reportLinkValue) {
      setReportLink(reportLinkValue);
    }
  }, []);

  useEffect(
    () => () => {
      dispatch(clearAccountsReportState());
    },
    [],
  );

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.accountsBalanceReportLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.accountsBalanceReportContent}>
        <div className={cn.accountsBalanceReportLeft}>
          <TableMenu
            counterIsHidden
            otherSearch={otherSearch}
            fieldNameCategoryId="categoryId"
            fieldNameGroupId="groupId"
            fieldNamePrimaryId="primaryId"
            headerText="Chart of account"
            onMenuToggle={() => {}}
            menuItems={menuItems}
            baseUrl="/reports/account-balance"
            onCategoryClick={(id: string) => {
              const nextFilters: Filters = {
                ...filters,
                sortProp: {
                  value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
                  type: SchemaFilterType.STRING,
                },
                sortDir: {
                  value: [sort.sortDir || SortDir.DESC],
                  type: SchemaFilterType.STRING,
                },
              };

              delete nextFilters.groupId;
              delete nextFilters.primaryId;
              delete nextFilters.excludeRevaluation;
              delete nextFilters.transferDateFrom;
              delete nextFilters.transferDateTo;
              delete nextFilters.transferDate;
              delete nextFilters.daysOffset;
              delete nextFilters.roundDecimals;
              delete nextFilters.search;

              if (id === categoryId && !filters.groupId && !filters.primaryId) {
                delete nextFilters.categoryId;
              } else {
                nextFilters.categoryId = {
                  value: [id],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter(nextFilters, true);
            }}
            onGroupClick={({ categoryId: menuCategoryId, id }) => {
              const nextFilters: Filters = {
                ...filters,
                categoryId: {
                  value: [menuCategoryId],
                  type: SchemaFilterType.STRING,
                },
                sortProp: {
                  value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
                  type: SchemaFilterType.STRING,
                },
                sortDir: {
                  value: [sort.sortDir || SortDir.DESC],
                  type: SchemaFilterType.STRING,
                },
              };

              delete nextFilters.primaryId;
              delete nextFilters.excludeRevaluation;
              delete nextFilters.transferDateFrom;
              delete nextFilters.transferDateTo;
              delete nextFilters.transferDate;
              delete nextFilters.daysOffset;
              delete nextFilters.roundDecimals;
              delete nextFilters.search;

              if (groupId === id && !filters.primaryId) {
                delete nextFilters.groupId;
              } else {
                nextFilters.groupId = {
                  value: [id],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter(nextFilters, true);
            }}
            onPrimaryAccountClick={({
              categoryId: menuCategoryId,
              groupId: menuGroupId,
              id,
            }) => {
              const nextFilters: Filters = {
                ...filters,
                categoryId: {
                  value: [menuCategoryId],
                  type: SchemaFilterType.STRING,
                },
                groupId: {
                  value: [menuGroupId],
                  type: SchemaFilterType.STRING,
                },
                sortProp: {
                  value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
                  type: SchemaFilterType.STRING,
                },
                sortDir: {
                  value: [sort.sortDir || SortDir.DESC],
                  type: SchemaFilterType.STRING,
                },
              };

              delete nextFilters.excludeRevaluation;
              delete nextFilters.transferDateFrom;
              delete nextFilters.transferDateTo;
              delete nextFilters.transferDate;
              delete nextFilters.daysOffset;
              delete nextFilters.roundDecimals;
              delete nextFilters.search;

              if (primaryId === id) {
                delete nextFilters.primaryId;
              } else {
                nextFilters.primaryId = {
                  value: [id],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter(nextFilters, true);
            }}
          />
        </div>
        <div className={cn.accountsBalanceReportRight}>
          <div className={cn.accountsBalanceReportControls}>
            <DateInputRange
              placeholder="All time"
              className={cn.accountsDateInput}
              values={dates}
              onChangeRange={(dateValues) => {
                setDates(
                  dateValues?.map((item) => new Date(item).toISOString()),
                );

                const nextValues: {
                  transferDateFrom?: string;
                  transferDateTo?: string;
                  excludeRevaluationValue?: boolean;
                  daysOffsetValue?: string;
                  roundDecimalsValues?: string;
                } = {
                  transferDateFrom: format(
                    new Date(dateValues[0]),
                    'yyyy-MM-dd',
                  ),
                  excludeRevaluationValue: excludeRevaluation,
                };

                if (dateValues?.length > 1) {
                  nextValues.transferDateTo = format(
                    new Date(dateValues[1]),
                    'yyyy-MM-dd',
                  );
                }

                // if (daysOffset) {
                //   nextValues.daysOffsetValue = daysOffset;
                // }

                if (roundDecimals) {
                  nextValues.roundDecimalsValues = roundDecimals;
                }

                onFilterReport(nextValues);
              }}
            />
            <ToggleInput
              name="excludeRevaluation"
              title="Exclude Revaluation"
              checked={excludeRevaluation}
              onChange={(e) => {
                setExcludeRevaluation(e.target.checked);

                const nextValues: {
                  transferDateFrom?: string;
                  transferDateTo?: string;
                  excludeRevaluationValue?: boolean;
                  daysOffsetValue?: string;
                  roundDecimalsValues?: string;
                } = {
                  excludeRevaluationValue: e.target.checked,
                };

                if (dates?.length) {
                  const [from, to] = dates;

                  nextValues.transferDateFrom = format(
                    new Date(from),
                    'yyyy-MM-dd',
                  );

                  if (to) {
                    nextValues.transferDateTo = format(
                      new Date(to),
                      'yyyy-MM-dd',
                    );
                  }
                }

                // if (daysOffset) {
                //   nextValues.daysOffsetValue = daysOffset;
                // }

                if (roundDecimals) {
                  nextValues.roundDecimalsValues = roundDecimals;
                }

                onFilterReport(nextValues);
              }}
              className={cn.balanceSheetVisibilityToggle}
              disabled={Boolean(errorList)}
            />

            {/* {reportLink?.includes('safeguarding') && (
              <InputText
                name="daysOffset"
                size={InputSize.SMALL}
                placeholder="Enter days offset"
                labelClassName={cn.daysOffsetWrapper}
                value={daysOffset}
                onChange={(e) => {
                  setDaysOffset(e.target.value);

                  const nextValues: {
                    transferDateFrom?: string;
                    transferDateTo?: string;
                    excludeRevaluationValue?: boolean;
                    daysOffsetValue?: string;
                    roundDecimalsValues?: string;
                  } = {
                    daysOffsetValue: e.target.value,
                    excludeRevaluationValue: excludeRevaluation,
                  };

                  if (dates?.length) {
                    const [from, to] = dates;

                    nextValues.transferDateFrom = format(
                      new Date(from),
                      'yyyy-MM-dd',
                    );

                    if (to) {
                      nextValues.transferDateTo = format(
                        new Date(to),
                        'yyyy-MM-dd',
                      );
                    }
                  }

                  if (roundDecimals) {
                    nextValues.roundDecimalsValues = roundDecimals;
                  }

                  onFilterReport(nextValues);
                }}
                disabled={Boolean(errorList)}
              />
            )} */}
            <div className={cn.precisionWrapper}>
              <PrecisionSelector
                precision={roundDecimals}
                setPrecision={(value) => {
                  setRoundDecimals(value);

                  const nextValues: {
                    transferDateFrom?: string;
                    transferDateTo?: string;
                    excludeRevaluationValue?: boolean;
                    daysOffsetValue?: string;
                    roundDecimalsValues?: string;
                  } = {
                    roundDecimalsValues: value,
                    excludeRevaluationValue: excludeRevaluation,
                  };

                  if (dates?.length) {
                    const [from, to] = dates;

                    nextValues.transferDateFrom = format(
                      new Date(from),
                      'yyyy-MM-dd',
                    );

                    if (to) {
                      nextValues.transferDateTo = format(
                        new Date(to),
                        'yyyy-MM-dd',
                      );
                    }
                  }

                  // if (daysOffset) {
                  //   nextValues.daysOffsetValue = daysOffset;
                  // }

                  onFilterReport(nextValues);
                }}
              />
            </div>
          </div>

          <div className={cn.accountsBalanceReportTable} ref={scrollRef}>
            <Table
              columns={columns}
              data={formattedData || []}
              onSort={(name) => onSort(name)}
              filters={filters}
              sort={sort}
              isLoading={isFetching || reportIsLoading || reportIsFetching}
              columnsLabels={columnsLabels}
              onFilterChange={(values) => {
                const nextValues = { ...values };

                delete nextValues.excludeRevaluation;
                delete nextValues.transferDateFrom;
                delete nextValues.transferDateTo;
                delete nextValues.transferDate;
                delete nextValues.daysOffset;
                delete nextValues.roundDecimals;

                onFilter(nextValues, true);
              }}
              filtersComponent={filtersComponent}
              handleSubmit={handleSubmit}
              storeKey="accounts-report-list"
              take="20"
              pagination={reportPagination}
              setPage={(data) => {
                const nextFilters = { ...filters };

                delete nextFilters.excludeRevaluation;
                delete nextFilters.transferDateFrom;
                delete nextFilters.transferDateTo;
                delete nextFilters.transferDate;
                delete nextFilters.daysOffset;
                delete nextFilters.roundDecimals;

                setPage({ ...data, filters: nextFilters });
              }}
              error={errorList}
              linkWithoutSearch
              clickable
              withSettings
              withScroll
            />
          </div>
        </div>
      </div>
    );
  }

  let backLink = stateProps.split('?')[0] || reportLink || ROUTES.REPORTS;

  if (dates?.length) {
    const [from, to] = dates;

    backLink = `${backLink}?transferDateFrom=${format(
      new Date(from),
      'yyyy-MM-dd',
    )}`;

    if (to) {
      backLink = `${backLink}&transferDateTo=${format(
        new Date(to),
        'yyyy-MM-dd',
      )}`;
    }
  }

  if (excludeRevaluation) {
    backLink = `${backLink}${
      backLink?.includes('?') ? '&' : '?'
    }excludeRevaluation=${excludeRevaluation}`;
  }

  // if (daysOffset) {
  //   backLink = `${backLink}${
  //     backLink?.includes('?') ? '&' : '?'
  //   }daysOffset=${daysOffset}`;
  // }

  return (
    <div className={cn.accountsBalanceReport}>
      <div className={cn.accountsBalanceReportBackLinkWrapper}>
        <NavLink
          to={backLink}
          className={cn.accountsBalanceReportBackLink}
          onClick={() => {
            dispatch(clearAccountsReportState());
            localStorage.removeItem('reportLink');
          }}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
      </div>
      <div className={cn.accountsBalanceReportTitle}>Accounts balances</div>
      {content}
    </div>
  );
};

export default AccountsBalanceReport;
